import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import { FC } from 'react';

import { BRAND_COLORS, CHART_COLORS } from '../../../constants/styles';

interface IDeltaIndicatorProps {
  /** Current value */
  current: number;
  /** Comparison value to calculate delta from */
  comparison: number;
  /** Whether higher is better (green) or worse (red). Default: true */
  higherIsBetter?: boolean;
  /** Size variant. Default: 'medium' */
  size?: 'small' | 'medium' | 'large';
  /** Show the indicator even when zero. Default: false */
  showZero?: boolean;
  /** Use lighter colors for visibility on dark backgrounds. Default: false */
  darkBackground?: boolean;
  /** Whether the values are already percentages. Default: false (quantities — % diff is calculated) */
  isPercentage?: boolean;
}

/**
 * DeltaIndicator - Shows {arrow} UP / DOWN / FLAT text
 * with color coding (green for improvement, red for decline)
 * and orange/neutral for no change.
 */
export const DeltaIndicator: FC<IDeltaIndicatorProps> = ({
  current,
  comparison,
  higherIsBetter = true,
  size = 'medium',
  showZero = false,
  darkBackground = false,
  isPercentage = false,
}) => {
  const delta = current - comparison;

  // Calculate percentage difference to determine FLAT threshold (<1%)
  // For percentage values: use absolute difference directly
  // For quantities: calculate relative % change from comparison
  const pctDiff = isPercentage
    ? Math.abs(delta)
    : comparison !== 0
    ? Math.abs((delta / comparison) * 100)
    : current !== 0
    ? 100 // went from 0 to something = 100% change
    : 0; // both zero

  const isNeutral = pctDiff < 1;

  // Don't show anything if neutral and showZero is false
  if (isNeutral && !showZero) {
    return null;
  }

  const isPositive = delta > 0;

  // Determine if the change is good or bad
  const isImprovement = isNeutral
    ? false
    : higherIsBetter
    ? isPositive
    : !isPositive;

  // Color based on improvement (green) or decline (red)
  // Neutral: Slate Gray on light bg, Light Gray on dark bg
  const color = isNeutral
    ? darkBackground
      ? BRAND_COLORS.secondary.lightGray // #a7b4c2
      : BRAND_COLORS.secondary.slateGray // #2f3a45
    : isImprovement
    ? CHART_COLORS.delta.positive
    : darkBackground
    ? '#ff9b9b'
    : CHART_COLORS.delta.negative;

  // Size-based styling
  const sizeStyles = {
    small: { fontSize: 11, iconMargin: 2 },
    medium: { fontSize: 12, iconMargin: 2 },
    large: { fontSize: 14, iconMargin: 3 },
  };

  const style = sizeStyles[size];

  // Label: UP / DOWN / FLAT
  const label = isNeutral ? 'FLAT' : isPositive ? 'UP' : 'DOWN';

  const arrowIcon = isNeutral ? (
    <ArrowRightOutlined style={{ marginRight: style.iconMargin }} />
  ) : isPositive ? (
    <ArrowUpOutlined style={{ marginRight: style.iconMargin }} />
  ) : (
    <ArrowDownOutlined style={{ marginRight: style.iconMargin }} />
  );

  return (
    <span
      style={{
        color,
        fontSize: style.fontSize,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {arrowIcon}
      {label}
    </span>
  );
};

export default DeltaIndicator;
